import safeStorage from './safeStorage';
import {
  generateX25519Identity,
  encodeSecretKeyB64,
  decodeSecretKeyB64,
} from './messageE2ee';

const LEGACY_SK = 'yona_messaging_x25519_sk_b64';
const LEGACY_PK = 'yona_messaging_x25519_pk_b64';
const STORAGE_OWNER_ID = 'yona_messaging_keys_owner_user_id';
const STORAGE_DEVICE_ID_LEGACY = 'yona_messaging_device_id_v1';

export function storageDeviceIdScopedKey(userId) {
  return `yona_messaging_device_id_v1_u${userId}`;
}

export function storageMessagingSkKey(userId) {
  return `yona_messaging_x25519_sk_b64_u${userId}`;
}

export function storageMessagingPkKey(userId) {
  return `yona_messaging_x25519_pk_b64_u${userId}`;
}

function localStorageHasAnyScopedMessagingDeviceId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('yona_messaging_device_id_v1_u')) return true;
    }
  } catch {
    // ignore
  }
  return false;
}

function removePerUserMessagingKeysFromBrowserStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (
        k &&
        (k.startsWith('yona_messaging_x25519_sk_b64_u') || k.startsWith('yona_messaging_x25519_pk_b64_u'))
      ) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => safeStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/** Decode SimpleJWT ``user_id`` (or numeric ``sub``) from access token. Shared by messaging paths. */
export function decodeAccessTokenUserId(accessToken) {
  try {
    const parts = accessToken.split('.');
    const payloadB64 = parts[1];
    if (!payloadB64) return null;
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4;
    const base64 = pad ? padded + '='.repeat(4 - pad) : padded;
    const json = atob(base64);
    const payload = JSON.parse(json);
    const raw = payload.user_id ?? payload.sub;
    if (raw == null) return null;
    const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function migrateLegacyMessagingKeysIfNeeded(userId) {
  const skKey = storageMessagingSkKey(userId);
  if (safeStorage.getItem(skKey)) return;

  const legSk = safeStorage.getItem(LEGACY_SK);
  const legPk = safeStorage.getItem(LEGACY_PK);
  if (!legSk || !legPk) return;

  const owner = safeStorage.getItem(STORAGE_OWNER_ID);
  if (owner != null && owner !== String(userId)) return;

  safeStorage.setItem(skKey, legSk);
  safeStorage.setItem(storageMessagingPkKey(userId), legPk);
  safeStorage.removeItem(LEGACY_SK);
  safeStorage.removeItem(LEGACY_PK);
  safeStorage.removeItem(STORAGE_OWNER_ID);
}

/** Tests, support, or explicit reset — legacy + all per-user messaging key slots. */
export function clearLocalMessagingKeys() {
  safeStorage.removeItem(LEGACY_SK);
  safeStorage.removeItem(LEGACY_PK);
  safeStorage.removeItem(STORAGE_OWNER_ID);
  removePerUserMessagingKeysFromBrowserStorage();
}

/**
 * Stable **per logged-in user** in this browser (not shared across accounts).
 * Migrates the legacy single key into the current user's slot once if no scoped ids exist yet.
 */
export function getOrCreateMessagingDeviceId() {
  const token = safeStorage.getItem('token');

  if (!token) {
    let id = safeStorage.getItem(STORAGE_DEVICE_ID_LEGACY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      safeStorage.setItem(STORAGE_DEVICE_ID_LEGACY, id);
    }
    return id;
  }

  const userId = decodeAccessTokenUserId(token);
  if (userId == null) {
    throw new Error(
      'Cannot resolve user id from access token — messaging device id unavailable. Try signing out and back in.'
    );
  }

  const scopedKey = storageDeviceIdScopedKey(userId);
  let id = safeStorage.getItem(scopedKey);
  if (id) return id;

  const legacy = safeStorage.getItem(STORAGE_DEVICE_ID_LEGACY);
  if (legacy && !localStorageHasAnyScopedMessagingDeviceId()) {
    safeStorage.setItem(scopedKey, legacy);
    safeStorage.removeItem(STORAGE_DEVICE_ID_LEGACY);
    return legacy;
  }

  id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  safeStorage.setItem(scopedKey, id);
  return id;
}

export function getLocalMessagingSecretKey() {
  const token = safeStorage.getItem('token');
  if (!token) return null;
  const userId = decodeAccessTokenUserId(token);
  if (userId == null) return null;
  const skB64 = safeStorage.getItem(storageMessagingSkKey(userId));
  if (!skB64) return null;
  try {
    return decodeSecretKeyB64(skB64);
  } catch {
    return null;
  }
}

/**
 * Ensure local X25519 key exists and public key is registered with the API.
 * @param {{ post: Function }} messagingService — messagingService from features/messaging/services
 */
export async function ensureMessagingIdentity(messagingService) {
  const accessToken = safeStorage.getItem('token');
  const userId = accessToken ? decodeAccessTokenUserId(accessToken) : null;
  if (userId == null) {
    throw new Error('Not logged in — cannot register messaging keys');
  }

  migrateLegacyMessagingKeysIfNeeded(userId);

  const skKey = storageMessagingSkKey(userId);
  const pkKey = storageMessagingPkKey(userId);
  let skB64 = safeStorage.getItem(skKey);
  let pkB64 = safeStorage.getItem(pkKey);
  if (!skB64 || !pkB64) {
    const id = generateX25519Identity();
    skB64 = encodeSecretKeyB64(id.secretKey);
    pkB64 = id.publicKeyB64;
    safeStorage.setItem(skKey, skB64);
    safeStorage.setItem(pkKey, pkB64);
  }
  await messagingService.registerMessagingPublicKey(pkB64);
  return { secretKey: decodeSecretKeyB64(skB64), publicKeyB64: pkB64 };
}
